import { SearchCombobox, SearchResult } from "@indigo-components";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AppointmentRecord,
  AppointmentStatus,
  AppointmentType,
  Language,
} from "~/api/API";
import {
  appointmentKeys,
  useAppointment,
  useAppointmentSearch,
} from "~/api/queries/appointments";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { useCurrentUser } from "~/auth/UserContext";
import { isInquiryPageStatus } from "~/common/appointmentConstants";
import { formatISODate } from "~/common/converters";
import { getPrimaryPatientCase, openInNewTab } from "~/common/utils";
import { DEFAULT_DATE_ISO, DEFAULT_TIME_ISO } from "~/constants";
import { UnauthorizedAccessError } from "~/error-handling/exceptions";
import { appointmentService } from "~/services/appointment";
import { isProdAtom } from "~/state/atoms";
import { isDoctorUserGroup, isHistorianUserGroup } from "~/types/auth";
import { formatDoctorDisplayName } from "~/utils/doctorName";
import {
  AppointmentStatusEnum,
  CommonStatusEnum,
  DoctorStatusEnum,
  getDisplayState,
  MAStatusEnum,
} from "./AppointmentStatusCell";

const NOT_SPECIFIED_PLACEHOLDER = "N/A";

const TYPE_MAP: Record<AppointmentType, string> = {
  [AppointmentType.QME]: "QME",
  [AppointmentType.QME_REEVALUATION]: "QME RE",
  [AppointmentType.AME]: "AME",
  [AppointmentType.AME_REEVALUATION]: "AME RE",
  [AppointmentType.SUPPLEMENTAL]: "SUPP",
  [AppointmentType.DEPOSITION]: "DEPO",
  [AppointmentType.BRIEFING]: "BRIEF",
  [AppointmentType.SIBTF]: "SIBTF",
  [AppointmentType.SIBTF_REEVALUATION]: "SIBTF RE",
  [AppointmentType.IME]: "IME",
  [AppointmentType.IME_REEVALUATION]: "IME RE",
  [AppointmentType.IME_ADR]: "IME ADR",
  [AppointmentType.IME_ADR_REEVALUATION]: "IME ADR RE",
  [AppointmentType.RECORD_REVIEW]: "REC REVIEW",
};

const getTypeLabel = (type: AppointmentType | null): string => {
  return type ? TYPE_MAP[type] : NOT_SPECIFIED_PLACEHOLDER;
};

const LANGUAGE_MAP: Record<Language, string> = {
  [Language.SPANISH]: "SPANISH",
  [Language.ENGLISH]: "ENGLISH",
  [Language.MANDARIN]: "MANDARIN",
  [Language.CANTONESE]: "CANTONESE",
  [Language.ARABIC]: "ARABIC",
  [Language.BENGALI]: "BENGALI",
  [Language.VIETNAMESE]: "VIETNAMESE",
  [Language.FARSI]: "FARSI",
  [Language.TAGALOG]: "TAGALOG",
  [Language.BURMESE]: "BURMESE",
  [Language.NORTH_AZERI]: "NORTH AZERI",
  [Language.PUNJABI]: "PUNJABI",
  [Language.ASL]: "ASL",
  [Language.MAM]: "MAM",
  [Language.ITALIAN]: "ITALIAN",
  [Language.TIGRINYA]: "TIGRINYA",
  [Language.TURKISH]: "TURKISH",
  [Language.FRENCH]: "FRENCH",
  [Language.PORTUGUESE]: "PORTUGUESE",
  [Language.OTHER]: "OTHER",
};

const getLanguageLabel = (language: Language | null): string => {
  return language ? LANGUAGE_MAP[language] : NOT_SPECIFIED_PLACEHOLDER;
};

// biome-ignore lint/suspicious/noRedeclare: distinct from the imported SearchResult component — separate type/value namespaces
interface SearchResult {
  id: string;
  patientName: string | null;
  doctorName: string | null;
  date: string | null;
  time: string | null;
  location: string | null;
  type: AppointmentType | null;
  claimNumber: string | null;
  language: Language | null;
}

interface CachedResult extends SearchResult {
  appointmentRecord?: AppointmentRecord;
  displayState?: AppointmentStatusEnum;
}

const SEARCH_MIN_CHARS = 2;

/**
 * SearchBar Component
 *
 * A search component that allows users to search for appointments by patient name or claim number.
 */
const statusColorMap: Record<string, string[]> = {
  errorVariant: [
    CommonStatusEnum.Cancelled,
    CommonStatusEnum.LateCancelled,
    CommonStatusEnum.InquiryClosed,
    CommonStatusEnum.NoShow,
    CommonStatusEnum.NoShowPatient,
    CommonStatusEnum.NoShowInterpreter,
    CommonStatusEnum.Rescheduled,
    CommonStatusEnum.LateRescheduled,
    CommonStatusEnum.RescheduledPatient,
    CommonStatusEnum.RescheduledADJ,
    CommonStatusEnum.RescheduledAADA,
    CommonStatusEnum.RescheduledInternal,
    CommonStatusEnum.LateRescheduledPatient,
    CommonStatusEnum.LateRescheduledADJ,
    CommonStatusEnum.LateRescheduledAADA,
    CommonStatusEnum.LateRescheduledInternal,
    MAStatusEnum.CorrectingPayments,
  ],
  doneVariant: [
    CommonStatusEnum.AppointmentCompleted,
    CommonStatusEnum.ReportSubmitted,
    DoctorStatusEnum.ReportApproved,
    MAStatusEnum.Billed,
    MAStatusEnum.ReadyToSubmit,
  ],
  todoVariant: [
    CommonStatusEnum.Inquiry,
    CommonStatusEnum.Scheduled,
    CommonStatusEnum.ReportReadyForReview,
    MAStatusEnum.ReadyForFinalReview,
  ],
  finalVariant: [CommonStatusEnum.PaymentReceived, MAStatusEnum.PaidPhysician],
};

const getStatusVariant = (
  status: AppointmentStatusEnum,
): "todo" | "done" | "error" | "final" => {
  if (statusColorMap.errorVariant.includes(status)) return "error";
  if (statusColorMap.doneVariant.includes(status)) return "done";
  if (statusColorMap.finalVariant.includes(status)) return "final";
  return "todo";
};

const SearchBar = ({
  indigoSize = "lg",
  indigoIsCollapsible = false,
}: {
  indigoSize?: "md" | "lg";
  indigoIsCollapsible?: boolean;
}) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [records, setRecords] = useState<CachedResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const { userGroup } = useAuthSession();
  const { user, isDemo } = useCurrentUser();
  const [isProd] = useAtom(isProdAtom);
  const queryClient = useQueryClient();

  // Normalize phone numbers by removing non-digit characters
  // Handles multiple patterns: "(555) 123-4567", "5551234567", "555-123-4567"
  // All normalize to: "5551234567"
  const normalizePhoneNumber = (input: string): string => {
    // Only process inputs that are at least 10 characters (shortest phone number)
    if (input.length < 10) {
      return input;
    }

    // Extract only digits from input
    const digitsOnly = input.replace(/\D/g, "");

    // Only normalize if we have 10-11 digits (US phone number with/without country code)
    if (digitsOnly.length >= 10 && digitsOnly.length <= 11) {
      return digitsOnly;
    }

    // Otherwise return original input (not a valid phone number)
    return input;
  };

  // Use the new search hook
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  const searchParams = useMemo(
    () => ({
      query: normalizePhoneNumber(debouncedQuery),
      isProd,
      userGroup,
      doctorId: user?.userDoctorId,
      isDemo,
    }),
    [debouncedQuery, isProd, userGroup, user?.userDoctorId, isDemo],
  );

  const { data: searchResults, isLoading } = useAppointmentSearch(
    searchParams,
    debouncedQuery.length >= SEARCH_MIN_CHARS,
  );

  // Debounce the query input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 200);

    return () => clearTimeout(timer);
  }, [query]);

  // Process search results when they change
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    if (!searchResults) {
      setRecords([]);
      return;
    }

    // Split search query into terms for matching
    const searchTerms = debouncedQuery.trim().split(/\s+/);

    const processedResults: CachedResult[] = searchResults.map((item) => {
      const matchedClaimNumber = findMatchingClaimNumber(
        item.primary_claim_number,
        item.other_claim_numbers,
        searchTerms,
      );

      return {
        id: item.id,
        patientName: item.patient_name || null,
        doctorName: item.doctor_name || null,
        date:
          !item.date || item.date === DEFAULT_DATE_ISO
            ? null
            : formatISODate(item.date),
        time: !item.time || item.time === DEFAULT_TIME_ISO ? null : item.time,
        location: item.location || null,
        type:
          AppointmentType[item.appt_type as keyof typeof AppointmentType] ||
          null,
        claimNumber: matchedClaimNumber,
        language: Language[item.language as keyof typeof Language] || null,
      };
    });

    setRecords(processedResults);
  }, [searchResults, debouncedQuery, userGroup]);

  // Handle opening/closing dropdown based on query and results
  useEffect(() => {
    if (query.length < SEARCH_MIN_CHARS) {
      setIsOpen(false);
    } else if (query.length >= SEARCH_MIN_CHARS) {
      setIsOpen(true);
    }
  }, [query]);

  // Fetch appointment details for all search results to enable status-based sorting.
  // Shares query keys with SearchResultWithStatus's useAppointment calls, so React Query deduplicates requests.
  const appointmentQueries = useQueries({
    queries: records.map((record) => ({
      queryKey: appointmentKeys.detail(record.id),
      queryFn: () =>
        appointmentService.fetchAppointmentById(
          record.id,
          userGroup ?? undefined,
        ),
      staleTime: 30 * 1000,
      // Don't retry authorization errors — the result won't change on retry
      retry: (failureCount: number, error: Error) => {
        if (error instanceof UnauthorizedAccessError) return false;
        return failureCount < 3;
      },
    })),
  });

  // For Doctor/Historian, results need filtering based on appointment details.
  // Wait for all detail queries to settle before showing results to avoid items
  // flashing then disappearing. Admin/MA don't need filtering, so show immediately.
  const needsFiltering =
    isDoctorUserGroup(userGroup) || isHistorianUserGroup(userGroup);
  const isDetailQueriesLoading =
    needsFiltering &&
    records.length > 0 &&
    appointmentQueries.some((q) => q.isLoading);

  // Filter and sort results by role and status.
  // - Doctor: hide inquiry-page appointments (INQUIRY / INQUIRY_CLOSED /
  //   REEVAL_HOLD — handled by MAs, not doctors)
  // - Historian: hide appointments the user cannot access (UnauthorizedAccessError),
  //   and enrich display fields from hydrated data (search only returns IDs for Historians)
  // - SCHEDULED appointments are promoted to the top while preserving relevance order.
  const isHistorian = isHistorianUserGroup(userGroup);
  const sortedRecords = (() => {
    if (records.length === 0) return records;

    const indexed = records.map((record, i) => {
      const queryResult = appointmentQueries[i];
      const appointmentRecord = queryResult?.data;
      // For Historians, search returns only IDs — populate display fields from hydrated data
      const enrichedRecord =
        isHistorian && appointmentRecord
          ? {
              ...record,
              ...mapAppointmentRecordToDisplayFields(appointmentRecord),
            }
          : record;
      return {
        record: enrichedRecord,
        i,
        status: appointmentRecord?.status,
        error: queryResult?.error,
      };
    });

    const filtered = indexed.filter(({ status, error }) => {
      if (isDoctorUserGroup(userGroup) && isInquiryPageStatus(status))
        return false;
      if (
        isHistorianUserGroup(userGroup) &&
        error instanceof UnauthorizedAccessError
      )
        return false;
      return true;
    });

    filtered.sort((a, b) => {
      const aScheduled = a.status === AppointmentStatus.SCHEDULED;
      const bScheduled = b.status === AppointmentStatus.SCHEDULED;
      if (aScheduled !== bScheduled) return aScheduled ? -1 : 1;
      return a.i - b.i; // Preserve original OpenSearch score order
    });

    return filtered.map(({ record }) => record);
  })();

  return (
    <div className={"relative"} ref={searchBarRef}>
      <SearchCombobox
        initialValue={query}
        onChange={setQuery}
        isLoading={isLoading || isDetailQueriesLoading}
        size={indigoSize}
        isCollapsible={indigoIsCollapsible}
        isOpen={isOpen}
        results={isDetailQueriesLoading ? [] : sortedRecords}
        placeholder="Search for patient, claim, or phone #"
        dropdownWidth={385}
        renderResult={(record: CachedResult) => (
          <SearchResultWithStatus record={record} />
        )}
        onResultSelect={(record: CachedResult) => {
          const appointmentRecord = queryClient.getQueryData<AppointmentRecord>(
            appointmentKeys.detail(record.id),
          );
          openAppointmentInNewTab(
            record.id,
            isInquiryPageStatus(appointmentRecord?.status),
          );
        }}
        onOutsideClick={() => setIsOpen(false)}
      />
    </div>
  );
};

const SearchResultWithStatus = ({ record }: { record: CachedResult }) => {
  // Use the individual appointment hook to get status
  const { data: appointmentRecord } = useAppointment(record.id);
  const { userGroup } = useAuthSession();

  // Determine display state from the fetched appointment record or cached data
  const displayState = useMemo(() => {
    if (record.displayState) {
      return record.displayState;
    }
    if (appointmentRecord) {
      return getDisplayState(userGroup, appointmentRecord);
    }
    return null;
  }, [record.displayState, appointmentRecord, userGroup]);

  const formattedResult = {
    title: `${record.patientName || NOT_SPECIFIED_PLACEHOLDER} <> ${
      record.doctorName || NOT_SPECIFIED_PLACEHOLDER
    }`,
    subtitle: `${record.date || ""} ${record.time || ""} @ ${
      record.location || NOT_SPECIFIED_PLACEHOLDER
    }`,
    details: `Claim #: ${record.claimNumber || NOT_SPECIFIED_PLACEHOLDER}`,
    status: displayState
      ? {
          label: displayState,
          variant: getStatusVariant(displayState),
        }
      : undefined,
    tags: [
      {
        label: getTypeLabel(record.type),
        variant: "outlined" as const,
        size: "medium" as const,
      },
      ...(record.language && record.language !== Language.ENGLISH
        ? [
            {
              label: getLanguageLabel(record.language),
              variant: "outlined" as const,
              size: "medium" as const,
            },
          ]
        : []),
    ],
  };

  return (
    <SearchResult
      {...formattedResult}
      onClick={() => {
        openAppointmentInNewTab(
          record.id,
          isInquiryPageStatus(appointmentRecord?.status),
        );
      }}
    />
  );
};

export default SearchBar;

/****************************/
/***** Helper Functions *****/
/****************************/

/** Maps hydrated AppointmentRecord fields to SearchResult display fields.
 * Used for Historian users where the search query only returns IDs. */
function mapAppointmentRecordToDisplayFields(
  record: AppointmentRecord,
): Omit<SearchResult, "id"> {
  const primaryPatientCase = getPrimaryPatientCase(record);
  const patient = primaryPatientCase?.patientCase?.patient;
  return {
    patientName: patient?.name || null,
    doctorName: record.doctor
      ? formatDoctorDisplayName(record.doctor) || null
      : null,
    date:
      !record.date || record.date === DEFAULT_DATE_ISO
        ? null
        : formatISODate(record.date),
    time: !record.time || record.time === DEFAULT_TIME_ISO ? null : record.time,
    location: record.location?.name || null,
    type: record.type || null,
    claimNumber: primaryPatientCase?.patientCase?.claimNumber || null,
    language: patient?.language || null,
  };
}

function findMatchingClaimNumber(
  primaryClaimNumber: string | null | undefined,
  otherClaimNumbers: (string | null)[] | null | undefined,
  searchTerms: string[],
): string | null {
  // Convert search terms to lowercase for case-insensitive matching
  const lowerSearchTerms = searchTerms.map((term) => term.toLowerCase());

  // First check primary claim number against all search terms
  if (primaryClaimNumber) {
    const lowerPrimary = primaryClaimNumber.toLowerCase();
    if (lowerSearchTerms.some((term) => lowerPrimary.includes(term))) {
      return primaryClaimNumber;
    }
  }

  // Then check other claim numbers against all search terms
  if (otherClaimNumbers?.length) {
    const validOtherClaims = otherClaimNumbers.filter(
      (claim): claim is string => !!claim,
    );
    for (const claim of validOtherClaims) {
      const lowerClaim = claim.toLowerCase();
      if (lowerSearchTerms.some((term) => lowerClaim.includes(term))) {
        return claim;
      }
    }
  }

  // If no matches found, return primary claim number or null
  return primaryClaimNumber || null;
}

function openAppointmentInNewTab(appointmentId: string, isInquiry: boolean) {
  const targetPath = isInquiry
    ? `/appointments/new/${appointmentId}`
    : `/appointments/${appointmentId}`;
  openInNewTab(targetPath);
}
